import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

// Generic draw-to-sign pad (renamed from w9_signature_controller when the
// deferred comped-agreement page (#9811) became its second host — the name
// must not lie about its scope). Hosts: the W-9 signing page, the
// deferred-agreement review-and-sign page, and the cart checkout screen
// (which hand-rolled its own raw-canvas pad until audit F14).
//
// Two ways to consume the same pad:
//
//   * submit-time — wire `submit->signature-pad#submit`. Fills the input
//     target(s) with a PNG data URL; an empty pad blocks submission with a
//     styled alert (never a native dialog). W-9 + deferred agreement.
//   * live — every finished stroke mirrors the data URL into the input
//     target(s) and announces `signature-pad:change` with `{ empty }`, so a
//     host controller can gate its own buttons on whether a signature exists
//     (cart_controller gates Checkout + Clear that way). This costs the
//     submit-time hosts nothing: `submit` still re-reads the pad, and `clear`
//     empties the inputs again.
//
// `input` is a MULTI-element target on purpose: checkout posts the same data
// URL into two hidden fields (authoritative `signature_data` plus the legacy
// `visible_signature_data` scratchpad), so every write goes to inputTargets.
//
// Dark mode note: the pad exports with a TRANSPARENT background, so only the
// ink lands in the PNG and the theme can never be baked into the artifact —
// which is what lets the same export drop cleanly onto the white PDF it gets
// embedded into. The ink color is therefore fixed per host rather than derived
// from data-bs-theme; the visible canvas gets a light surface from CSS
// (.w9-signature-pad in
// app/assets/stylesheets/goatleads/components/_w9_signing.scss) so the stroke
// stays visible on a dark-mode card.
export default class extends Controller {
  // `inkCheck` (optional) posts how the last submit-time pixel check went —
  // diagnostics only, so a server-side blank_pad refusal can say whether the
  // canvas pixels were readable when the browser let the form go (#21066).
  static targets = ["canvas", "input", "submit", "inkCheck"]

  // Checkout has always signed in red; the other hosts use signature_pad's
  // default black. An explicit value keeps that host difference in the markup
  // instead of forking the controller.
  static values = { penColor: String }

  connect() {
    this.resizeCanvas()
    this.pad = new SignaturePad(this.canvasTarget, this.padOptions())
    this.endStrokeHandler = () => this.syncInputs()
    this.pad.addEventListener("endStroke", this.endStrokeHandler)
  }

  disconnect() {
    this.pad?.removeEventListener?.("endStroke", this.endStrokeHandler)
    this.pad?.off?.()
  }

  clear() {
    this.pad.clear()
    this.writeInputs("")
    this.notifyChange()
  }

  // Everything that can refuse runs synchronously BEFORE preventDefault has to
  // have happened — there is no await on this path, so the form can never post
  // between the decision and the cancel.
  submit(event) {
    if (!this.prepareSubmit()) event.preventDefault()
  }

  // The submit-time check, callable without a submit event. A host that posts
  // with `form.submit()` (cart_controller's modal confirmations) fires NO submit
  // event, so `submit->signature-pad#submit` never runs for it — that path
  // posted whatever the last endStroke had exported, unchecked, and Mac Safari
  // buyers were refused server-side as blank_pad after signing (#21066). Such a
  // host calls this first and posts only on `true`.
  //
  // Returns true with a fresh export in the inputs, or false after telling the
  // signer to draw (and resetting a pad whose canvas lost its strokes).
  prepareSubmit() {
    const padEmpty = this.padIsEmpty()
    const inkCheck = padEmpty ? "pad_empty" : this.inkCheck()
    this.writeInkCheck(inkCheck)
    if (padEmpty || inkCheck === "no_ink") {
      // The pad remembers strokes the canvas no longer shows (its backing store
      // was reset after drawing). Posting that export would reach the server as
      // a fully transparent PNG and be refused as "please sign" AFTER the buyer
      // did sign (#12061) — reset both so the pad and the canvas agree again.
      // (cart_controller re-derives the Checkout button from the announced
      // change, so a refused submit never leaves the buyer with a dead button.)
      if (!padEmpty) this.clear()
      window.showAlertModal("Please draw your signature before submitting.")
      return false
    }
    this.writeInputs(this.dataUrl())
    return true
  }

  // A pad that never came up — connect() threw before `new SignaturePad`,
  // typically because the canvas target is missing from the host markup — has
  // nothing to export. Treat it as empty so submit() refuses (and says so)
  // instead of throwing past preventDefault and letting an unsigned form post.
  padIsEmpty() {
    return !this.pad || this.pad.isEmpty()
  }

  // Does the canvas hold at least one non-transparent pixel? ("ink" / "no_ink") `pad.isEmpty()`
  // answers from signature_pad's stroke list, which survives a canvas reset the
  // pixels do not; the export is taken from the pixels. When the context cannot
  // expose pixel data — no getImageData (jsdom, some embedded engines), or a
  // read that THROWS (a fingerprinting blocker such as CanvasBlocker raises on
  // pixel reads; a zero-sized canvas raises IndexSizeError) — there is nothing
  // to check against, so the pad's own answer stands. The throw must not escape:
  // it would leave submit() before preventDefault and let the form post
  // unguarded, which is worse than the pre-#12061 behaviour it replaces.
  //
  // Only "no_ink" refuses. The other answers name why the pixels could not be
  // read, for the diagnostic field (#21066).
  inkCheck() {
    if (!this.hasCanvasTarget) return "no_canvas"
    const canvas = this.canvasTarget
    const context = canvas.getContext("2d")
    if (!context || typeof context.getImageData !== "function") return "unsupported"
    let data
    try {
      ({ data } = context.getImageData(0, 0, canvas.width, canvas.height))
    } catch {
      return "unreadable"
    }
    for (let alpha = 3; alpha < data.length; alpha += 4) {
      if (data[alpha] !== 0) return "ink"
    }
    return "no_ink"
  }

  writeInkCheck(value) {
    this.inkCheckTargets.forEach((input) => { input.value = value })
  }

  // Runs when a stroke finishes (and after clear()): the inputs always hold
  // the CURRENT pad, never a stale export.
  syncInputs() {
    this.writeInputs(this.pad.isEmpty() ? "" : this.dataUrl())
    this.notifyChange()
  }

  dataUrl() {
    return this.pad.toDataURL("image/png")
  }

  // Each write announces itself as a bubbling `input` event, as typing would:
  // a card-dirty form around the pad (W-9 signing, CRM terms) fingerprints the
  // hidden field, so its Save turns on when a signature is drawn and back off
  // when the pad is cleared (BUTTON-9). A programmatic `value =` fires nothing.
  writeInputs(value) {
    this.inputTargets.forEach((input) => {
      if (input.value === value) return
      input.value = value
      input.dispatchEvent(new Event("input", { bubbles: true }))
    })
  }

  notifyChange() {
    this.dispatch("change", { detail: { empty: this.pad.isEmpty() } })
  }

  padOptions() {
    const options = { backgroundColor: "rgba(0,0,0,0)" }
    if (this.hasPenColorValue) options.penColor = this.penColorValue
    return options
  }

  // Scale the canvas backing store for high-DPI screens so strokes aren't blurry.
  resizeCanvas() {
    const canvas = this.canvasTarget
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const width = canvas.offsetWidth || canvas.width
    const height = canvas.offsetHeight || canvas.height
    canvas.width = width * ratio
    canvas.height = height * ratio
    canvas.getContext("2d").scale(ratio, ratio)
  }
}
